import React from 'react';

import { Tooltip } from 'antd-v5';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { luxonViewDateFormat } from '@shared/constants';

/**
 * The audit fields every advancement type carries some subset of. Ranks have no
 * counselor and merit badges do, but a step with no date drops out on its own.
 */
export interface AdvancementAuditInfo {
  markedCompletedDate?: string | null;
  markedCompletedFirstName?: string | null;
  markedCompletedLastName?: string | null;
  counselorApprovedDate?: string | null;
  counselorApprovedFirstName?: string | null;
  counselorApprovedLastName?: string | null;
  leaderApprovedDate?: string | null;
  leaderApprovedFirstName?: string | null;
  leaderApprovedLastName?: string | null;
  awardedDate?: string | null;
  awardedFirstName?: string | null;
  awardedLastName?: string | null;
}

// Sign-off order, so the tooltip reads as a history
const auditSteps = [
  {
    id: 'markedCompleted',
    date: (item: AdvancementAuditInfo) => item.markedCompletedDate,
    name: (item: AdvancementAuditInfo) => [
      item.markedCompletedFirstName,
      item.markedCompletedLastName,
    ],
  },
  {
    id: 'counselorApproved',
    date: (item: AdvancementAuditInfo) => item.counselorApprovedDate,
    name: (item: AdvancementAuditInfo) => [
      item.counselorApprovedFirstName,
      item.counselorApprovedLastName,
    ],
  },
  {
    id: 'leaderApproved',
    date: (item: AdvancementAuditInfo) => item.leaderApprovedDate,
    name: (item: AdvancementAuditInfo) => [
      item.leaderApprovedFirstName,
      item.leaderApprovedLastName,
    ],
  },
  {
    id: 'awarded',
    date: (item: AdvancementAuditInfo) => item.awardedDate,
    name: (item: AdvancementAuditInfo) => [
      item.awardedFirstName,
      item.awardedLastName,
    ],
  },
];

export function formatAuditDate(date?: string | null) {
  if (!date) return '';
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat(luxonViewDateFormat) : '';
}

/** The dated sign-off lines the tooltip renders, in history order (empty if none) */
export function getAuditLines(advancement: AdvancementAuditInfo) {
  return auditSteps
    .map(step => ({
      id: step.id,
      date: formatAuditDate(step.date(advancement)),
      name: step.name(advancement).filter(Boolean).join(' '),
    }))
    .filter(line => !!line.date);
}

/** Whether the advancement carries any sign-off to audit, so the tooltip has content */
export function hasAuditInfo(advancement: AdvancementAuditInfo) {
  return getAuditLines(advancement).length > 0;
}

export interface AdvancementAuditTooltipProps {
  advancement: AdvancementAuditInfo;
  children: React.ReactElement;
}

/** Who signed the advancement off and when */
export default function AdvancementAuditTooltip(
  props: AdvancementAuditTooltipProps,
) {
  const { advancement, children } = props;

  const lines = getAuditLines(advancement);

  if (!lines.length) return children;

  return (
    <Tooltip
      placement="bottom"
      title={lines.map(line => (
        <div key={line.id}>
          <FormattedMessage
            id={`advancementAudit.${line.id}`}
            values={{ name: line.name, date: line.date }}
          />
        </div>
      ))}
    >
      {children}
    </Tooltip>
  );
}
